import { Box, Grid, styled, Typography } from "@mui/material";
import { FC } from "react";
import { GiftCardResultsInterface } from "../../pages/gift-card/GiftCard";
import { formatCurrency } from "../../utils/formatCurrency";
import { extraordinaryColors } from "../../utils/theme";

const StyledBox = styled(Box)<{
  textcolor: string;
  backgroundcolor: string;
}>(({ textcolor, backgroundcolor }) => ({
  fontWeight: 700,
  display: "flex",
  textAlign: "center",
  justifyContent: "center",
  alignItems: "center",
  color: textcolor,
  backgroundColor: backgroundcolor,
  width: "100%",
  height: 60,
  fontSize: 30,
  borderRadius: 15,
  "&:hover": {
    backgroundColor: backgroundcolor
  },
  fontFamily: "GT-Alpina-Condensed",
  marginTop: 8
}));

export const GiftCardResults: FC<{
  neverSpentPercentage: number;
  breakagePercentage: number;
  cardCost: number;
  results: GiftCardResultsInterface;
}> = ({ neverSpentPercentage, breakagePercentage, cardCost, results }) => {
  return (
    <Grid
      container
      spacing={2}
      mt={0.5}
      mb={2}
      sx={{ maxWidth: "800px", margin: "0 auto" }}
    >
      <Grid item xs={6}>
        <Typography fontWeight={700} fontSize={20}>
          Total gift card value
        </Typography>
        <StyledBox
          textcolor={extraordinaryColors.pink.main}
          backgroundcolor={extraordinaryColors.pink.light}
        >
          {formatCurrency(results.totalGiftCardValue)}
        </StyledBox>
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={20}>
          Gift cards never spent
        </Typography>

        <Grid container spacing={1} width="100%">
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.white.main}
            >
              {(neverSpentPercentage * 100).toFixed(0)}%
            </StyledBox>
          </Grid>

          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.pink.light}
            >
              {formatCurrency(results.neverSpent)}
            </StyledBox>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={20}>
          Card costs (per card)
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.white.main}
            >
              {formatCurrency(cardCost)}
            </StyledBox>
          </Grid>

          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.pink.light}
            >
              {formatCurrency(results.totalCardCost)}
            </StyledBox>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={20}>
          Breakage
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.white.main}
            >
              {(breakagePercentage * 100).toFixed(0)}%
            </StyledBox>
          </Grid>
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.pink.main}
              backgroundcolor={extraordinaryColors.pink.light}
            >
              {formatCurrency(results.breakage)}
            </StyledBox>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={20}>
          Total
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.white.main}
              backgroundcolor={extraordinaryColors.pink.main}
            >
              {(results.totalBreakagePercentage * 100).toFixed(0)}%
            </StyledBox>
          </Grid>
          <Grid item xs={6}>
            <StyledBox
              textcolor={extraordinaryColors.white.main}
              backgroundcolor={extraordinaryColors.pink.main}
            >
              {formatCurrency(results.totalBreakage)}
            </StyledBox>
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ p: 3, mt: 3 }}>
        <Typography fontWeight={600} gutterBottom fontSize={12}>
          The results from this calculator are estimates based on industry
          averages and global data. This information is intended for general
          guidance only and should not be taken as professional financial or tax
          advice. Actual gift card breakage and unspent funds may vary based on
          your specific business, industry, or region. We recommend consulting a
          financial professional or tax adviser for guidance tailored to your
          circumstances. We make no guarantees regarding the accuracy,
          completeness, or applicability of the information provided by this
          tool.
        </Typography>
      </Box>
    </Grid>
  );
};
